import { motion } from "framer-motion";
import { formatCurrency } from "../../../data/mock/dashboardData";

export default function RegionalCollectionsChart({ regions: regionsProp } = {}) {
  const regions = (() => {
    if (!Array.isArray(regionsProp) || regionsProp.length === 0) return [];
    const max = Math.max(
      1,
      ...regionsProp.map((r) => Number(r.amount ?? r.collected ?? r.outstanding ?? r.total) || 0)
    );
    return regionsProp.slice(0, 8).map((r) => {
      const amount = Number(r.amount ?? r.collected ?? r.outstanding ?? r.total) || 0;
      return {
        name: r.name || r.label || "Unknown",
        amount,
        pct: Math.max(amount > 0 ? 4 : 0, Math.round((amount / max) * 100)),
        accounts: Number(r.accounts ?? r.total ?? 0),
      };
    });
  })();

  if (!regions.length) {
    return (
      <p className="flex min-h-[220px] flex-1 items-center justify-center text-sm text-primary/40">
        No regional data yet.
      </p>
    );
  }

  const total = regions.reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="flex h-full min-h-[220px] flex-1 flex-col">
      <div className="flex flex-1 flex-col justify-between gap-1.5">
        {regions.map((region, i) => (
          <div key={region.name} className="group flex flex-1 items-center gap-3">
            <span className="w-[5.5rem] shrink-0 truncate text-[11px] font-medium text-primary/55 sm:w-28">
              {region.name}
            </span>
            <div className="relative min-w-0 flex-1">
              <div className="h-7 overflow-hidden rounded-full bg-primary/[0.05] sm:h-8">
                <motion.div
                  className="flex h-full items-center rounded-full bg-gradient-to-r from-primary to-primary-light pl-2 group-hover:from-primary-light group-hover:to-accent"
                  initial={{ width: 0 }}
                  animate={{ width: `${region.pct}%` }}
                  transition={{ duration: 0.7, delay: 0.08 + i * 0.08, ease: "easeOut" }}
                >
                  {region.pct >= 28 && (
                    <span className="truncate px-1 text-[9px] font-bold text-white/90">
                      {region.pct}%
                    </span>
                  )}
                </motion.div>
              </div>
            </div>
            <div className="w-16 shrink-0 text-right">
              <span className="block text-[11px] font-bold leading-tight text-primary">
                {formatCurrency(region.amount)}
              </span>
              <span className="text-[9px] text-primary/35">{region.accounts} accts</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 border-t border-primary/[0.06] pt-3">
        <div className="rounded-lg bg-primary/[0.03] px-2.5 py-2">
          <p className="text-[10px] font-medium text-primary/40">Total shown</p>
          <p className="mt-0.5 text-sm font-bold text-primary">{formatCurrency(total)}</p>
        </div>
        <div className="rounded-lg bg-accent/10 px-2.5 py-2">
          <p className="text-[10px] font-medium text-primary/40">Top region</p>
          <p className="mt-0.5 truncate text-sm font-bold text-primary">{regions[0]?.name}</p>
        </div>
      </div>
    </div>
  );
}
